import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  type?: "website" | "article" | "hotel";
  image?: string;
  structuredData?: object | object[];
  noindex?: boolean;
}

/**
 * SEOHead Component - Uses react-helmet-async for per-page SEO meta tags
 * Ensures each page has unique title, description, canonical, and social tags
 */
const SEOHead = ({
  title,
  description,
  canonical,
  type = "website",
  image = "https://www.extendedstayharborheights.com/logo.png",
  structuredData,
  noindex = false,
}: SEOHeadProps) => {
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      {canonical && !noindex && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Harbor Heights Extended Stay" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;

// Re-export structured data templates from the original SEO component for convenience
export { 
  hotelStructuredData, 
  localBusinessStructuredData, 
  faqStructuredData, 
  breadcrumbStructuredData 
} from "./SEO";
